'use client';

import { Button, cn, Span, useBoolean } from '@scatterlab/zeta-core';
import { Context, memo, useCallback, useMemo } from 'react';
import { match } from 'ts-pattern';

import DropdownMenu from './dropdown-menu/DropdownMenu';
import DropdownMenuItem from './dropdown-menu/DropdownMenuItem';
import useDropdownMenu from './hooks/useDropdownMenu';
import { ArrowDown16Icon } from './icons';

export type SelectProps<TItemValue extends string | number = string> = {
  value?: TItemValue;
  placeholder?: string;
  className?: string;
  labelClassName?: string;
  iconClassName?: string;
  options: {
    label: string;
    value: TItemValue;
  }[];
  onClickOption: (value: TItemValue) => void;
  dropdownAlign?: 'left' | 'right' | 'fill';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contexts?: Context<any>[];
  tabIndex?: number;
  disabled?: boolean;
};

const Select = <TItemValue extends string | number = string>({
  className,
  value,
  placeholder,
  options,
  onClickOption,
  labelClassName,
  iconClassName,
  dropdownAlign = 'right',
  contexts,
  tabIndex,
  disabled,
}: SelectProps<TItemValue>) => {
  const {
    value: visibleDropdown,
    toggle: toggleDropdown,
    off: hideDropdown,
  } = useBoolean();

  const { ref, setDropdownItemCurrentPosition, dropdownPosition } =
    useDropdownMenu(visibleDropdown);

  const handlePressToggleDropdown = useCallback(() => {
    setDropdownItemCurrentPosition();
    toggleDropdown();
  }, [setDropdownItemCurrentPosition, toggleDropdown]);

  const selectedLabel = useMemo(
    () => options.find(({ value: v }) => v === value)?.label || placeholder,
    [placeholder, value, options],
  );

  return (
    <>
      <Button
        tabIndex={tabIndex}
        ref={ref}
        type="button"
        onPress={() => handlePressToggleDropdown()}
        className={cn(
          'flex w-full flex-row items-center justify-between rounded-[10px] border border-white/[8%] bg-white/5 px-3 py-[15px] hover:border-primary-400 hover:bg-gray-main',
          { 'bg-gray-main border-primary-400': visibleDropdown },
          className,
        )}
        disabled={disabled}
      >
        <Span
          className={cn(
            'body14',
            {
              'text-white/50': !value && !!placeholder,
              'text-white': !!value,
            },
            labelClassName,
          )}
        >
          {selectedLabel}
        </Span>
        <ArrowDown16Icon
          className={cn(
            'size-4 text-white',
            visibleDropdown ? 'rotate-180' : 'rotate-0',
            iconClassName,
          )}
        />
      </Button>
      {dropdownPosition && (
        <DropdownMenu
          open={visibleDropdown}
          containerClassName="max-h-[200px] overflow-y-auto scrollbar-hide"
          scrollable
          onClose={hideDropdown}
          className="w-full"
          top={dropdownPosition.top + 8}
          {...match(dropdownAlign)
            .with('fill', () => ({
              left: dropdownPosition.left,
              right: dropdownPosition.right,
            }))
            .with('left', () => ({
              left: dropdownPosition.left,
            }))
            .with('right', () => ({
              right: dropdownPosition.right,
            }))
            .exhaustive()}
          contexts={contexts}
        >
          {options.map(({ label, value: optionValue }, index) => (
            <DropdownMenuItem
              content={label}
              key={label}
              onPress={() => {
                onClickOption(optionValue);
                hideDropdown();
              }}
              first={index === 0}
              last={index === options.length - 1}
              className="w-full"
            />
          ))}
        </DropdownMenu>
      )}
    </>
  );
};

export default memo(Select) as typeof Select;
